/**
 * Condition Watch — Condition B/C candidate analyzer (AMB-9).
 *
 * Governance that watches itself: cross-references the signals the workspace
 * has on record against the governance acts that changed — or ratified — the
 * plan, and surfaces the two failure conditions of the Signal Framework
 * (docs/1-canon/23-signal-framework.md §2) as CANDIDATES:
 *
 *   condition_b_candidate  A material signal past the policy window with no
 *                          governance act after it — "was this a deliberate
 *                          hold?"
 *   condition_c_candidate  A governance act with no material signal in the
 *                          window before it — "what signal motivated this
 *                          change?"
 *
 * CANON FIDELITY (non-negotiable, Doc 23): the POR condition is set ONLY by a
 * human via set_por_condition, rationale required, NEVER inferred. This agent
 * produces candidate findings for human review — it never sets any condition
 * and never touches any authority state. Detection proposes; authority
 * disposes.
 *
 * Data grounding:
 *   SIGNALS (60-day lookback)
 *   - agent_findings status='active', severity critical/high (migration 494),
 *     EXCLUDING condition_watch's own findings — load-bearing: without the
 *     exclusion every B candidate this agent emits would become next run's
 *     "signal", a feedback loop that never quiets.
 *   - item_impact_events (migration 356) — severity is an INTEGER 1..5, not a
 *     severity word; >= 4 is material (5 → critical, 4 → high).
 *   - stakeholder_pulse_trends view (migration 582) rows where not_addressed
 *     dominates responses (not_addressed_count > addressed_count +
 *     partially_addressed_count), dated by last_response_at.
 *   ACTS (60-day lookback — the evidenceBrief.ts idioms)
 *   - rescoring_events (migration 534), workspace_id direct.
 *   - reprioritization_triggers (migration 078), workspace scope via the
 *     plan_cycles!inner join.
 *   - catchball_balls (migration 440), workspace_id direct.
 *   - governance_events event_type='priority_list_approved' (migrations
 *     246/250/562), dated by occurred_at.
 *
 * Detection is exact maths; the LLM only narrates, with deterministic fallback
 * copy that frames every candidate as a question for the humans holding the
 * right — never a verdict.
 *
 * The pure detection core lives in supabase/functions/_shared/intelligenceCores.ts
 * (shared with the ambient edge function); this file keeps the client-side data
 * loading and batch orchestration.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { createFinding, clearActiveFindings, type NewFinding } from '../agentFindingsService';
import { narrateFinding } from './llmIntelligenceService';
import {
  detectConditionCandidates,
  CONDITION_WATCH_PERSONA,
  CONDITION_WATCH_WINDOW_DAYS,
  MAX_FINDINGS,
  type GovSignal,
  type GovAct,
  type CoreSeverity,
} from '../../../supabase/functions/_shared/intelligenceCores';

export const CONDITION_WATCH_AGENT_TYPE = 'condition_watch';

// Re-exports so tests and other importers use the same core the runtimes run.
export { detectConditionCandidates };
export type { GovSignal, GovAct };

/** How far back signals and acts are loaded. */
const LOOKBACK_DAYS = 60;
/** item_impact_events.severity (integer 1..5, migration 356) at/above this is material. */
const IMPACT_SEVERITY_FLOOR = 4;

// Display names per owner decision (2026-07-10, see intelligenceScan.ts):
// Fracture is the user-facing name for agent_type 'cassandra'.
const SIGNAL_AGENT_DISPLAY: Record<string, string> = {
  cassandra: 'Fracture',
  compass: 'Compass',
  keystone: 'Keystone',
  meridian: 'Meridian',
};

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// ── Data loading ─────────────────────────────────────────────────────────────────

async function loadItemTitles(
  supabase: ReturnType<typeof getSupabaseClient>,
  itemIds: string[],
): Promise<Map<string, string>> {
  const titles = new Map<string, string>();
  const ids = [...new Set(itemIds.filter(Boolean))];
  if (ids.length === 0) return titles;
  const { data } = await supabase.from('items').select('id, title').in('id', ids);
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    titles.set(r.id as string, (r.title as string) ?? '');
  }
  return titles;
}

async function loadSignals(workspaceId: string, sinceIso: string): Promise<GovSignal[]> {
  const supabase = getSupabaseClient();
  const signals: GovSignal[] = [];

  // Material findings from the other intelligence agents. Excluding
  // condition_watch's own rows prevents the feedback loop (see header).
  const { data: findingRows } = await supabase
    .from('agent_findings')
    .select('id, agent_type, severity, title, created_at')
    .eq('workspace_id', workspaceId)
    .eq('status', 'active')
    .in('severity', ['critical', 'high'])
    .neq('agent_type', CONDITION_WATCH_AGENT_TYPE)
    .gte('created_at', sinceIso)
    .limit(500);
  for (const r of (findingRows ?? []) as Record<string, unknown>[]) {
    const agentType = r.agent_type as string;
    signals.push({
      kind: 'agent_finding',
      severity: r.severity as CoreSeverity,
      occurredAt: r.created_at as string,
      ref: r.id as string,
      label: `${SIGNAL_AGENT_DISPLAY[agentType] ?? agentType} finding “${r.title as string}”`,
    });
  }

  // High-impact business events. severity is an integer 1..5 (migration 356).
  const { data: impactRows } = await supabase
    .from('item_impact_events')
    .select('id, item_id, event_type, severity, description, occurred_at')
    .eq('workspace_id', workspaceId)
    .gte('severity', IMPACT_SEVERITY_FLOOR)
    .gte('occurred_at', sinceIso)
    .limit(200);
  for (const r of (impactRows ?? []) as Record<string, unknown>[]) {
    const sev = Number(r.severity);
    signals.push({
      kind: 'impact_event',
      severity: sev >= 5 ? 'critical' : 'high',
      occurredAt: r.occurred_at as string,
      ref: r.id as string,
      label:
        `Impact event: ${(r.event_type as string).replace(/_/g, ' ')} (severity ${sev}/5) — ` +
        truncate((r.description as string) ?? '', 120),
    });
  }

  // Pulse trends where "not addressed" dominates responses (migration 582
  // view columns: responded/addressed/partially_addressed/not_addressed
  // counts, last_response_at).
  const { data: trendRows } = await supabase
    .from('stakeholder_pulse_trends')
    .select(
      'priority_set_id, responded_count, addressed_count, partially_addressed_count, ' +
        'not_addressed_count, last_response_at',
    )
    .eq('workspace_id', workspaceId)
    .limit(50);
  for (const r of (trendRows ?? []) as Record<string, unknown>[]) {
    const responded = Number(r.responded_count) || 0;
    const notAddressed = Number(r.not_addressed_count) || 0;
    const addressed =
      (Number(r.addressed_count) || 0) + (Number(r.partially_addressed_count) || 0);
    const lastResponseAt = r.last_response_at as string | null;
    if (responded === 0 || notAddressed <= addressed) continue;
    if (!lastResponseAt || lastResponseAt < sinceIso) continue;
    signals.push({
      kind: 'pulse_trend',
      severity: 'high',
      occurredAt: lastResponseAt,
      ref: r.priority_set_id as string,
      label:
        `Stakeholder pulse on priority list ${(r.priority_set_id as string).slice(0, 8)}: ` +
        `${notAddressed} of ${responded} responses say their input was not addressed`,
    });
  }

  return signals;
}

async function loadActs(workspaceId: string, sinceIso: string): Promise<GovAct[]> {
  const supabase = getSupabaseClient();
  const acts: GovAct[] = [];

  // Rescorings (migration 534).
  const { data: rescoreRows } = await supabase
    .from('rescoring_events')
    .select('id, created_at, item_id, attribute_key, previous_score, revised_score')
    .eq('workspace_id', workspaceId)
    .gte('created_at', sinceIso)
    .limit(100);
  const rescores = (rescoreRows ?? []) as Record<string, unknown>[];
  const titles = await loadItemTitles(supabase, rescores.map((r) => r.item_id as string));
  for (const r of rescores) {
    acts.push({
      kind: 'rescoring_event',
      occurredAt: r.created_at as string,
      ref: r.id as string,
      label:
        `“${titles.get(r.item_id as string) || 'An item'}” was rescored: ` +
        `${r.attribute_key as string} ${r.previous_score as number} → ${r.revised_score as number}`,
    });
  }

  // Reprioritization triggers — workspace scope via plan_cycles (the
  // evidenceBrief.ts idiom; migration 078).
  const { data: trigRows } = await supabase
    .from('reprioritization_triggers')
    .select('id, created_at, status, plan_cycles!inner(workspace_id, name)')
    .eq('plan_cycles.workspace_id', workspaceId)
    .gte('created_at', sinceIso)
    .limit(100);
  for (const r of (trigRows ?? []) as Record<string, unknown>[]) {
    const cycle = (r.plan_cycles ?? {}) as Record<string, unknown>;
    const cycleName = typeof cycle.name === 'string' ? cycle.name : 'a plan cycle';
    acts.push({
      kind: 'reprioritization_trigger',
      occurredAt: r.created_at as string,
      ref: r.id as string,
      label: `Reprioritization trigger (status "${r.status as string}") opened on "${cycleName}"`,
    });
  }

  // Catchball balls (migration 440).
  const { data: ballRows } = await supabase
    .from('catchball_balls')
    .select('id, created_at, name')
    .eq('workspace_id', workspaceId)
    .gte('created_at', sinceIso)
    .limit(100);
  for (const r of (ballRows ?? []) as Record<string, unknown>[]) {
    acts.push({
      kind: 'catchball_ball',
      occurredAt: r.created_at as string,
      ref: r.id as string,
      label: `Catchball ball “${r.name as string}” was composed`,
    });
  }

  // Ratifications (migrations 246/250/562).
  const { data: approvalRows } = await supabase
    .from('governance_events')
    .select('id, occurred_at')
    .eq('workspace_id', workspaceId)
    .eq('event_type', 'priority_list_approved')
    .gte('occurred_at', sinceIso)
    .limit(100);
  for (const r of (approvalRows ?? []) as Record<string, unknown>[]) {
    acts.push({
      kind: 'governance_event',
      occurredAt: r.occurred_at as string,
      ref: r.id as string,
      label: 'A priority list was approved (ratification)',
    });
  }

  return acts;
}

// ── Batch runner ─────────────────────────────────────────────────────────────────

export async function runConditionWatchBatch(
  workspaceId: string,
  _scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };

  const sinceIso = new Date(Date.now() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const signals = await loadSignals(workspaceId, sinceIso);
  const acts = await loadActs(workspaceId, sinceIso);
  stats.processed = signals.length + acts.length;

  let drafts = detectConditionCandidates(signals, acts, {
    windowDays: CONDITION_WATCH_WINDOW_DAYS,
    nowIso: new Date().toISOString(),
  });
  drafts = drafts.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);

  if (drafts.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  await clearActiveFindings(workspaceId, CONDITION_WATCH_AGENT_TYPE);

  const total = drafts.length;
  let done = 0;
  for (const draft of drafts) {
    if (signal.aborted) break;

    const narration = await narrateFinding({
      persona: draft.persona ?? CONDITION_WATCH_PERSONA,
      findingKind: draft.findingKind,
      evidence: draft.base.data ?? {},
      systemPrompt: config.systemPrompt,
      model: config.model,
    });

    const finding: NewFinding = {
      ...draft.base,
      summary: narration?.summary || draft.fallbackSummary,
      detail: narration?.detail || draft.fallbackDetail,
    };

    const { error } = await createFinding(workspaceId, CONDITION_WATCH_AGENT_TYPE, finding);
    if (error) stats.errors++; else stats.proposed++;

    done++;
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return stats;
}
